import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import './Reservations.css';
import { format, isToday, parseISO, isFuture, isEqual } from 'date-fns';
import airbnbLogo from '../assets/airbnb.png';
import bookingLogo from '../assets/booking.png';
import vrboLogo from '../assets/vrbo.png';
import ReservationPopup from '../components/ReservationPopup';

// Helper function to format time from API
const formatTime = (timeString) => {
  if (!timeString) return '';
  
  try {
    // Ensure timeString is a string
    const timeValue = String(timeString);
    
    // If it's already a formatted time (HH:MM format)
    if (timeValue.includes(':')) {
      const [hours, minutes] = timeValue.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    
    // If it's a Date object
    if (timeString instanceof Date) {
      const hours = timeString.getHours();
      const minutes = timeString.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;
    }
    
    // If it's a number (24-hour format) or can be parsed as a number
    const hour = parseInt(timeValue, 10);
    if (!isNaN(hour)) {
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:00 ${ampm}`;
    }
    
    // If we couldn't parse it in any of the expected formats
    return timeValue;
  } catch (e) {
    console.error('Error formatting time:', e);
    // Return a safe string representation or empty string if conversion fails
    return timeString ? String(timeString) : '';
  }
};

// Add a helper function to render the channel image
const renderChannelIcon = (channelName) => {
  if (!channelName) return '-';
  const channel = channelName.toLowerCase();
  const channelMapping = {
    airbnbofficial: airbnbLogo,
    bookingcom: bookingLogo,
    homeaway: vrboLogo
  };
  if (channelMapping[channel]) {
    return (
      <img
        src={channelMapping[channel]}
        alt={channelName}
        className="ChannelIcon"
        style={{ width: '30px', height: '30px' }}
      />
    );
  } else {
    return channelName;
  }
};

function Reservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState('check-ins'); // Default to check-ins instead of 'all'
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showReservationPopup, setShowReservationPopup] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Define fetchReservations outside useEffect so it can be called from elsewhere
  const fetchReservations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Step 1: Get reservations with new or modified status
      let query = supabase
        .from('reservations')
        .select(`
          id, 
          listing_map_id, 
          channel_name, 
          channel_reservation_id, 
          external_property_id, 
          guest_portal_url, 
          reservation_date, 
          guest_first_name, 
          guest_last_name, 
          number_of_guests, 
          adults, 
          children, 
          infants, 
          pets, 
          arrival_date, 
          departure_date, 
          nights, 
          total_price, 
          tax_amount, 
          phone, 
          stripe_guest_id, 
          channel_commission_amount, 
          cleaning_fee, 
          security_deposit_fee, 
          status, 
          payment_status, 
          revenue_per_day, 
          deposit_paid, 
          early_check_in_paid, 
          early_check_in_time, 
          late_check_out_paid, 
          late_check_out_time, 
          eta, 
          etd, 
          checked_out,
          conversation_notes,
          guest_rating, 
          conversation_summary
        `)
        .filter('status', 'in', '("new","modified")')
        .filter('departure_date', 'gte', new Date().toISOString().split('T')[0]);
      
      // Add search filter if searchText is not empty
      if (searchText) {
        // Search by guest name (first name or last name)
        // Using ilike for case-insensitive search with wildcard
        query = query.or(`guest_first_name.ilike.%${searchText}%,guest_last_name.ilike.%${searchText}%`);
      }

      const { data: reservationsData, error: reservationsError } = await query;

      if (reservationsError) throw reservationsError;

      console.log('reservationsData', reservationsData);

      // Filter out reservations with invalid dates
      const validReservations = reservationsData
      
      if (validReservations.length === 0) {
        setReservations([]);
        setLoading(false);
        return;
      }
      
      // Step 2: Get listings for the reservations (previously Step 3)
      const listingMapIds = validReservations
        .map(res => res.listing_map_id)
        // .filter(id => id !== null && id !== undefined);
        
      const { data: listingsData, error: listingsError } = listingMapIds.length > 0 
        ? await supabase
            .from('listings')
            .select(`
              id, 
              name, 
              internal_listing_name, 
              airbnb_listing_url, 
              vrbo_listing_url
            `)
            .in('id', listingMapIds)
        : { data: [], error: null };
        
      if (listingsError) throw listingsError;
      
      // Step 3: Get preAuth transactions for the reservations
      const reservationIds = validReservations.map(res => res.id);
      
      const { data: transactionsData, error: transactionsError } = reservationIds.length > 0
        ? await supabase
            .from('transactions')
            .select(`
              id,
              reservation_id,
              type,
              title,
              description,
              currency,
              payment_method,
              amount,
              captured_amount,
              status,
              payment_provider,
              payment_provider_message,
              charge_date,
              hold_release_date
            `)
            .in('reservation_id', reservationIds)
            .eq('type', 'preAuth')
        : { data: [], error: null };
        
      if (transactionsError) throw transactionsError;
      
      console.log('transactionsData', transactionsData);
      
      // Step 4: Join the data manually
      const transformedData = validReservations.map(reservation => {
        // Find related listing
        const listing = listingsData.find(list => 
          list.id === reservation.listing_map_id
        );
        
        // Find related transactions
        const transactions = transactionsData.filter(transaction => 
          transaction.reservation_id === reservation.id
        );
        
        return {
          id: reservation.id,
          reservation: reservation,
          listing: listing || null,
          transactions: transactions || [],
          conversation: null // Set conversation to null since we're no longer fetching it
        };
      });
      
      setReservations(transformedData);
      
    } catch (error) {
      setError('Failed to fetch reservations: ' + error.message);
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };
  
  // Call fetchReservations when component mounts or searchText changes
  useEffect(() => {
    fetchReservations();
  }, [searchText]); // Include searchText in dependency array

  // Filter reservations based on sort option
  const filteredReservations = reservations.filter(item => {
    try {
      if (!item.reservation?.arrival_date || !item.reservation?.departure_date) return false;
        
      const arrivalDate = parseISO(item.reservation.arrival_date);
      const departureDate = parseISO(item.reservation.departure_date);
      
      // For all check-ins or all check-outs based on tab
      if (sortOption === 'check-ins') {
        // Show all future check-ins including today
        return isFuture(arrivalDate) || isToday(arrivalDate);
      } else if (sortOption === 'check-outs') {
        // Show all future check-outs including today
        return isFuture(departureDate) || isToday(departureDate);
      }
      
      return false; // Default case, shouldn't hit this
    } catch (e) {
      console.error('Error filtering reservation:', e, item);
      return false;
    }
  });

  // Group reservations by date
  const groupedReservations = filteredReservations.reduce((groups, item) => {
    try {
      // Use arrival_date for check-ins tab and departure_date for check-outs tab
      const dateKey = sortOption === 'check-ins' 
        ? item.reservation.arrival_date 
        : item.reservation.departure_date;
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    } catch (e) {
      console.error('Error grouping reservation:', e, item);
      return groups;
    }
  }, {});

  // Sort dates
  const sortedDates = Object.keys(groupedReservations).sort((a, b) => {
    try {
      // Check if both dates are valid before comparing
      if (!a || !b) {
        console.error('Invalid date encountered:', { a, b });
        return 0;
      }
      
      // Parse the dates to compare them
      const dateA = parseISO(a);
      const dateB = parseISO(b);
      
      // Check if parsing was successful
      if (!dateA || isNaN(dateA.getTime()) || !dateB || isNaN(dateB.getTime())) {
        console.error('Date parsing failed:', { a, b, dateA, dateB });
        return 0;
      }
      
      return dateA.getTime() - dateB.getTime();
    } catch (error) {
      console.error('Error sorting dates:', error, { a, b });
      return 0;
    }
  });

  // Function to handle viewing reservation details
  const handleViewDetails = (item) => {
    // Add debugging logs
    console.log('handleViewDetails called with item:', item);
    
    // Create the proper data structure expected by ReservationPopup
    const reservationData = {
      reservations: item.reservation,
      listings: item.listing,
      recipient_name: `${item.reservation.guest_first_name} ${item.reservation.guest_last_name}`,
      // If you have a profile picture, you can add it here, otherwise use a placeholder
      recipient_picture: 'https://via.placeholder.com/50',  
    };
    
    console.log('Created reservationData:', reservationData);
    setSelectedReservation(reservationData);
    setShowReservationPopup(true);
    console.log('State updated, showReservationPopup:', true);
  };

  return (
    <div className="ReservationsContainer">
      <div className="ReservationsHeader">
        <h1>Reservations</h1>
        <div className={`ReservationsSearch ${isSearching ? 'searching' : ''}`}>
          <form onSubmit={(e) => {
            e.preventDefault();
            setIsSearching(true);
            fetchReservations();
          }}>
            <input
              type="text"
              placeholder="Search by guest name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="ReservationsSearchInput"
            />
            <button 
              type="submit" 
              className="ReservationsSearchButton"
              title={searchText ? "Search" : "Search reservations"}
            >
              {searchText ? <i className="fas fa-search"></i> : <i className="fas fa-search"></i>}
            </button>
          </form>
          {searchText && (
            <button 
              className="ReservationsClearSearch" 
              onClick={() => {
                setSearchText('');
                setIsSearching(false);
              }}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="ReservationsTabs">
          <button 
            className={`ReservationsTab ${sortOption === 'check-ins' ? 'active' : ''}`}
            onClick={() => setSortOption('check-ins')}
          >
            <i className="fas fa-sign-in-alt"></i> Check-ins by Day
          </button>
          <button 
            className={`ReservationsTab ${sortOption === 'check-outs' ? 'active' : ''}`}
            onClick={() => setSortOption('check-outs')}
          >
            <i className="fas fa-sign-out-alt"></i> Check-outs by Day
          </button>
        </div>
      </div>

      <div className="ReservationsContentWrapper">
        {loading ? (
          <div className="ReservationsLoadingContainer">
            <div className="ReservationsLoadingSpinner"></div>
            <p>Loading reservations...</p>
          </div>
        ) : error ? (
          <div className="ReservationsErrorMessage">
            <p>{error}</p>
          </div>
        ) : filteredReservations.length === 0 ? (
          <div className="ReservationsNoResults">
            {searchText ? (
              <p>No reservations found matching "{searchText}". Try a different search term.</p>
            ) : (
              <p>No reservations found for the selected filter.</p>
            )}
          </div>
        ) : (
          <div className="ReservationsList">
            {searchText && (
              <div className="ReservationsSearchResults">
                <p>
                  <i className="fas fa-search"></i> 
                  Showing {filteredReservations.length} results for "{searchText}"
                  <button 
                    onClick={() => {
                      setSearchText('');
                      setIsSearching(false);
                    }}
                    className="ReservationsSearchResultsClear"
                  >
                    Clear search
                  </button>
                </p>
              </div>
            )}
            {sortedDates.map(date => (
              <div key={date} className="ReservationsDateGroup">
                <div className="ReservationsDateHeader">
                  <h2>
                    {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                    {sortOption === 'check-ins' && <span className="ReservationsDateContext"> (Check-ins)</span>}
                    {sortOption === 'check-outs' && <span className="ReservationsDateContext"> (Check-outs)</span>}
                  </h2>
                </div>
                <div className="ReservationsTable">
                  <table>
                    <thead>
                      <tr>
                        <th>Channel</th>
                        <th>Guest</th>
                        <th>Property</th>
                        <th>Dates</th>
                        <th>Guests</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th>Card</th>
                        <th>Deposit</th>
                        <th>Early-In</th>
                        <th>ETA</th>
                        <th>Late-Out</th>
                        <th>ETD</th>
                        <th>Checked Out</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedReservations[date].map(item => (
                        <tr key={item.id} className={`ReservationsRow ${item.reservation.status.toLowerCase()}`}>
                          <td className="ReservationsChannel">
                            {renderChannelIcon(item.reservation.channel_name)}
                          </td>
                          <td className="ReservationsGuest">
                            {item.reservation.guest_first_name} {item.reservation.guest_last_name}
                          </td>
                          <td className="ReservationsProperty">
                            <span>{item.listing?.internal_listing_name || 'Unknown Property'}</span>
                          </td>
                          <td className="ReservationsDates">
                            <div>{format(parseISO(item.reservation.arrival_date), 'MMM d')} - {format(parseISO(item.reservation.departure_date), 'MMM d')}</div>
                            <div className="ReservationsNights">{item.reservation.nights} nights</div>
                          </td>
                          <td className="ReservationsGuests">
                            {item.reservation.number_of_guests} total
                          </td>
                          <td className="ReservationsTotal">
                            ${parseFloat(item.reservation.total_price).toFixed(2)}
                          </td>
                          <td className="ReservationsStatusCell">
                            <span className={`ReservationsStatusBadge ${item.reservation.status.toLowerCase()}`}>
                              {item.reservation.status}
                            </span>
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.payment_status === 'Paid' ? (
                              <span className="ReservationsCheckmark">
                                <i className="fas fa-check-circle"></i>
                              </span>
                            ) : (
                              <span className="ReservationsCross">
                                <i className="fas fa-times-circle"></i>{item.reservation.payment_status}
                              </span>
                            )}
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.stripe_guest_id ? (
                              <span className="ReservationsCheckmark">
                                <i className="fas fa-check-circle"></i>
                              </span>
                            ) : (
                              <span className="ReservationsCross">
                                <i className="fas fa-times-circle"></i>
                              </span>
                            )}
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.deposit_paid || 
                             (item.transactions && 
                              item.transactions.some(t => t.status === 'holdSuccess')) ? (
                              <span className="ReservationsCheckmark">
                                <i className="fas fa-check-circle"></i>
                              </span>
                            ) : (
                              <span className="ReservationsCross">
                                <i className="fas fa-times-circle"></i>
                              </span>
                            )}
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.early_check_in_paid ? (
                              <span title={formatTime(item.reservation.early_check_in_time)}>
                                {formatTime(item.reservation.early_check_in_time)}
                              </span>
                            ) : (
                              <span className="ReservationsDash">-</span>
                            )}
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.eta ? (
                              <span title={formatTime(item.reservation.eta)}>
                                {formatTime(item.reservation.eta)}
                              </span>
                            ) : (
                              <span className="ReservationsDash">-</span>
                            )}
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.late_check_out_paid ? (
                              <span title={formatTime(item.reservation.late_check_out_time)}>
                                {formatTime(item.reservation.late_check_out_time)}
                              </span>
                            ) : (
                              <span className="ReservationsDash">-</span>
                            )}
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.etd ? (
                              <span title={formatTime(item.reservation.etd)}>
                                {formatTime(item.reservation.etd)}
                              </span>
                            ) : (
                              <span className="ReservationsDash">-</span>
                            )}
                          </td>
                          <td className="ReservationsInfoCell">
                            {item.reservation.checked_out ? (
                              <span className="ReservationsCheckmark">
                                <i className="fas fa-check-circle"></i>
                              </span>
                            ) : (
                              <span className="ReservationsCross">
                                <i className="fas fa-times-circle"></i>
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="ReservationsActions">
                              <button title="Message guest" className="ReservationsActionBtn">
                                <i className="fas fa-comment"></i>
                              </button>
                              <button 
                                title="View details" 
                                className="ReservationsActionBtn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('Info button clicked for item:', item);
                                  handleViewDetails(item);
                                }}
                              >
                                <i className="fas fa-info-circle"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Render the ReservationPopup component when a reservation is selected */}
      {(
        <ReservationPopup 
          reservationData={selectedReservation || {
            reservations: {},
            listings: {},
            recipient_name: "",
            recipient_picture: 'https://via.placeholder.com/50'
          }}
          showEntireReservation={showReservationPopup}
          setShowEntireReservation={setShowReservationPopup}
        />
      )}
    </div>
  );
}

export default Reservations;
